<template>
  <div class="db-card db-card-event">
    <table class="db-card-event-table">
      <!-- Accessible table title -->
      <caption v-if="event">
        <span class="db-visually-hidden">
          {{ event.name }}.
          <template v-if="$slots.schedule"> {{ $t('Schedule') }}:&nbsp; </template>
          {{
            displayStartEndTime
              ? $t('{x} to {y}', {
                  x: timeSlotFormatting(timeSlot.startTime),
                  y: timeSlotFormatting(timeSlot.endTime),
                })
              : timeSlotFormatting(timeSlot.startTime)
          }}
        </span>
      </caption>

      <thead>
        <!-- Mobile specific header -->
        <tr v-if="isMobile">
          <td colspan="4">
            <div
              class="db-event-table-title"
              aria-hidden="true"
            >
              {{ event.name }}
              <span
                v-if="timeSlot.startTime && timeSlot.endTime"
                class="db-event-table-schedule"
              >
                <IconDbClock
                  width="16"
                  height="16"
                />&nbsp;
                {{
                  displayStartEndTime
                    ? $t('{x} to {y}', {
                        x: timeSlotFormatting(timeSlot.startTime),
                        y: timeSlotFormatting(timeSlot.endTime),
                      })
                    : timeSlotFormatting(timeSlot.startTime)
                }}
              </span>
            </div>
          </td>
        </tr>
        <!-- !Mobile specific header -->
        <tr v-if="!isMobile">
          <td
            colspan="5"
            class="db-card-event-table-title db-event-table-title"
            aria-hidden="true"
          >
            {{ event.name }}
          </td>
        </tr>
        <tr>
          <!-- Desktop specific header -->
          <td v-if="!isMobile">
            <div
              class="db-event-table-title"
              aria-hidden="true"
            >
              <span
                v-if="timeSlot.startTime && timeSlot.endTime"
                class="db-event-table-schedule"
              >
                <IconDbClock
                  width="16"
                  height="16"
                />&nbsp;
                {{
                  displayStartEndTime
                    ? $t('{x} to {y}', {
                        x: timeSlotFormatting(timeSlot.startTime),
                        y: timeSlotFormatting(timeSlot.endTime),
                      })
                    : timeSlotFormatting(timeSlot.startTime)
                }}
              </span>
            </div>
          </td>
          <!-- !Desktop specific header -->
          <th
            :id="`th-price-${uid}`"
            :scope="!isMobile ? `col` : undefined"
            v-if="!(items.length === 0 && addons.length === 0)"
          >
            {{ $t('Price') }}
          </th>
          <th
            :id="`th-quantity-${uid}`"
            :scope="!isMobile ? `col` : undefined"
            v-if="!(items.length === 0 && addons.length === 0)"
          >
            {{ $t('Quantity') }}
          </th>
          <th
            :id="`th-quantity-${uid}`"
            :scope="!isMobile ? `col` : undefined"
            v-if="!(items.length === 0 && addons.length === 0)"
          >
            {{ $t('Total') }}
          </th>
          <th
            :id="`th-action-${uid}`"
            :scope="!isMobile ? `col` : undefined"
            v-if="!(items.length === 0 && addons.length === 0)"
          >
            {{ $t('Edit') }}
          </th>
        </tr>
      </thead>

      <tbody v-if="!(items.length === 0 && addons.length === 0) || hasAddons">
        <!-- Items -->
        <!-- No items from api data - PackageData.items is empty -->
        <tr v-if="items.length === 0 && addons.length === 0 && hasAddons">
          <td colspan="5">
            <p>{{ $t('No items') }}</p>
            <p>
              {{ $t('Click on + Add more below to check the available add-ons for this event.') }}
            </p>
          </td>
        </tr>
        <!-- With items from api data - PackageData.items is filled -->
        <PackageRow
          v-for="(item, index) in items"
          v-else
          :key="`item${index}`"
          :parentUid="uid"
          :isMobile="isMobile"
          :name="item.name"
          :quantity="item.quantity"
          class="db-card-event-inclusion db-package-divider"
        />

        <!-- Addons -->
        <!-- With items from api data - PackageData.items is filled -->
        <PackageRow
          v-for="(addOnItem, index) in addons"
          :key="`addon${index}`"
          :parentUid="uid"
          :addonId="addOnItem.addonId"
          :isMobile="isMobile"
          :name="addOnItem.name"
          :quantity="addOnItem.quantity"
          :currencyCode="addOnItem.currencyCode"
          :price="addOnItem.price"
          :total="addOnItem.quantity * (Math.round(addOnItem.price * 100) / 100)"
          class="db-card-event-addon db-package-divider"
          v-on:edit="$emit('edit', $event)"
          v-on:delete="$emit('delete', $event)"
        />
      </tbody>
    </table>
  </div>

  <div
    v-if="hasAddons"
    class="db-event-addmore"
  >
    <button
      type="button"
      class="db-button-text-link db-bold"
      v-on:click="$emit('add-more')"
    >
      {{ `+ ${$t('Add more')}` }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { v4 as uuidv4 } from 'uuid'
import { useBreakpoints } from '@vueuse/core'
import type {
  PackageEvent,
  PackageItem,
  CardAddonsItem,
  PackageTimeSlot,
} from './types/packageData'
import { timeSlotFormatting } from '@/services/config-date'
import { usePackagesStore } from '@/stores/packages'

const packageStore = usePackagesStore()

withDefaults(
  defineProps<{
    event: PackageEvent
    /** Amount of package inclusions */
    timeSlot?: PackageTimeSlot
    items?: PackageItem[]
    addons?: CardAddonsItem[]
    hasAddons?: boolean
  }>(),
  {
    items: () => [],
    addons: () => [],
    hasAddons: false,
    timeSlot: () => ({
      startTime: '',
      endTime: '',
    }),
  },
)

defineEmits<{
  (e: 'delete', index: string): void
  (e: 'edit', index: string): void
  (e: 'add-more'): void
}>()

/** Unique id */
const uid = uuidv4()
/** Table layout depends on breakpoint */
const breakpoints = useBreakpoints({ desktop: 992 })
const isMobile = breakpoints.smaller('desktop')
const displayStartEndTime = packageStore.displayStartEndTimeTimeSlot() < 2
</script>

<style>
@import '@/assets/styles/commons/_cards.css';
@import '@/assets/styles/components/cards/_event.css';

.db-package-divider:not(:last-child) {
  border: none;
  border-bottom: 1px solid var(--db_borders_color);
}
</style>
